import { cn } from '@/lib/utils';
import { Button } from '@startupquickstart/frontend';
import { Heart } from 'lucide-react';
import { useEffect, useState } from 'react';
import { guessWordStatus, wordLetterSet } from './minigame-logic';
import { parseGuessWord } from './types';
import { DemoNotConfigured } from './DemoChrome';
import type { DemoChallenge } from './types';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** Web replica of the mobile hangman-style guess-word minigame (preview only). */
export function GuessWordDemo({ challenge }: { challenge: DemoChallenge }) {
  const { word, category, maxGuesses } = parseGuessWord(challenge.minigameConfig);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  useEffect(() => setPicked(new Set()), [challenge.id]);

  if (word.length < 2) {
    return (
      <DemoNotConfigured>
        This word challenge isn&apos;t fully configured yet — the answer word is missing.
      </DemoNotConfigured>
    );
  }

  const letters = wordLetterSet(word);
  const { state, remaining } = guessWordStatus(word, picked, maxGuesses);

  const pick = (letter: string) => {
    if (state !== 'playing' || picked.has(letter)) return;
    setPicked((prev) => new Set(prev).add(letter));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
          Guess the word
        </span>
        <span className="flex items-center gap-1">
          {Array.from({ length: maxGuesses }).map((_, i) => (
            <Heart
              key={i}
              className={cn('h-3.5 w-3.5', i < remaining ? 'text-red-500' : 'text-border')}
              fill={i < remaining ? 'currentColor' : 'transparent'}
            />
          ))}
        </span>
      </div>

      {!!category && (
        <p className="text-xs text-muted-foreground">
          Category: <span className="text-foreground">{category}</span>
        </p>
      )}

      <div className="flex flex-wrap items-end justify-center gap-x-2 gap-y-3 rounded-2xl border bg-muted/30 p-4">
        {word.split('').map((char, i) => {
          const isLetter = /[A-Z]/.test(char);
          const revealed = !isLetter || picked.has(char);
          return (
            <span
              key={i}
              className={cn(
                'min-w-[22px] text-center text-2xl font-bold',
                revealed ? 'text-foreground' : 'border-b-2 border-border text-transparent',
              )}
            >
              {revealed ? char : '_'}
            </span>
          );
        })}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {ALPHABET.split('').map((letter) => {
          const isPicked = picked.has(letter);
          const isCorrect = isPicked && letters.has(letter);
          const isWrong = isPicked && !letters.has(letter);
          return (
            <button
              key={letter}
              type="button"
              onClick={() => pick(letter)}
              disabled={isPicked || state !== 'playing'}
              className={cn(
                'flex h-10 w-9 items-center justify-center rounded-lg border text-base font-semibold',
                isCorrect && 'border-emerald-500 bg-emerald-500 text-white',
                isWrong && 'border-red-500 bg-red-500 text-white',
                !isPicked && 'bg-card text-foreground hover:bg-muted',
                isPicked && !isCorrect && !isWrong && 'opacity-50',
              )}
            >
              {letter}
            </button>
          );
        })}
      </div>

      {state === 'won' && (
        <p className="rounded-xl bg-emerald-50 p-3 text-center text-sm font-semibold text-emerald-700">
          Got it! The word was {word}.
        </p>
      )}
      {state === 'lost' && (
        <p className="rounded-xl bg-red-50 p-3 text-center text-sm font-medium text-red-700">
          Out of guesses. The word was {word}.
        </p>
      )}

      {(state === 'lost' || state === 'won') && (
        <Button variant="outline" onClick={() => setPicked(new Set())}>
          {state === 'won' ? 'Play again' : 'Try again'}
        </Button>
      )}
    </div>
  );
}
